// src/hooks/useSessionLogic.js
import { useEffect, useRef, useState } from 'react';
import { getAlertDetails } from '../alertHelper.js';
import { getAddressFromCoordinates } from '../geoHelper.js';
import { getAlertMediaType, isAlertSessionType } from '../apiHelper.js';

import { useSessionData } from './useSessionData.js';
import { useSessionMap } from './useSessionMap.js';
import { useSessionMedia } from './useSessionMedia.js';
import { computeTimelineSegments } from '../timelineHelper.js';
import { selectSharedClockPosition } from '../mediaClockHelper.js';
import { captureSessionAccess } from '../sessionAccess.js';
import { buildSessionJournal, buildTimelineMarkers } from '../sessionJournal.js';

export const SessionType = Object.freeze({
    LIVEMAPALERTE: 'LIVEMAPALERTE',
    MANUAL: 'MANUAL',
    SHORTCUT: 'SHORTCUT',
    WIDGET: 'WIDGET',
    SHAKE_DETECTED: 'SHAKE_DETECTED',
    ACCIDENT_DETECTED: 'ACCIDENT_DETECTED',
    ITINERARY_STOPPED: 'ITINERARY_STOPPED',
    LIVE_SHARE_STOPPED: 'LIVE_SHARE_STOPPED',
    ITINERARY_NORMAL: 'ITINERARY_NORMAL',
    FOLLOW: 'FOLLOW',
    ITINERARY_SHARED: 'ITINERARY_SHARED',
    INACTIVE: 'INACTIVE'
});

/**
 * Options (vue relecture du dashboard) : `sessionId` et `apiUrl` au lieu de
 * l'adresse de la page ; `layout: 'investigation'` affiche vidéo et carte
 * ensemble et tient le journal de la session. Sans option : le direct.
 */
/** Message d'accès particulier : lien fermé après la fin de la session (page de confidentialité). */
export const SESSION_EXPIRED = 'SESSION_EXPIRED';

export const useSessionLogic = ({ refs, setAlertDetails, setAddress, setViewState, sessionId: givenSessionId = null, apiUrl = null, layout = 'live' }) => {
    const isInvestigation = layout === 'investigation';
    const [journal, setJournal] = useState([]);
    const [timelineMarkers, setTimelineMarkers] = useState([]);
    const initializationGenerationRef = useRef(0);
    const [statusMessage, setStatusMessage] = useState(null);
    const [accessMessage, setAccessMessage] = useState(null);
    const [sessionState, setSessionState] = useState(null);
    const [isHistoricalMode, setIsHistoricalMode] = useState(false);
    const [canGoLive, setCanGoLive] = useState(null);

    const sessionId = givenSessionId || window.location.pathname.split('/')[2];
    // Sur le domaine officiel, le site (www) et l'API (api) sont deux hosts
    // distincts servis par le même backend. Ailleurs (nip.io, localhost),
    // on garde l'origine courante pour ne rien casser.
    const BACKEND_API_URL = apiUrl || (window.location.hostname === 'www.monalerte.app'
        ? 'https://api.monalerte.app'
        : window.location.origin);
    const sessionData = useSessionData(BACKEND_API_URL);
    const sessionMap = useSessionMap(sessionData.virtualSessionRef, sessionData.stateRef);
    const sessionMedia = useSessionMedia(sessionId, BACKEND_API_URL, sessionData.sessionMediaKeyRef);

    const sessionContextRef = useRef({
        sessionMediaType: 'gps',
        currentDisplayMode: 'video-only',
        currentSessionType: null
    });

    const recomputeTimelineRef = useRef(null);
    const mediaClockContextRef = useRef({ sessionStartTs: 0, fallbackOffset: 0 });
    const addressRequestVersionRef = useRef(0);

    const updateAddressFromLocation = (location, allowGeocoding = true) => {
        if (!location) return;
        const requestVersion = ++addressRequestVersionRef.current;
        if (location.addressName && location.addressName !== 'Position de départ') {
            setAddress(location.addressName);
            return;
        }
        const lat = location.latitude ?? location.lat;
        const lng = location.longitude ?? location.lng;
        if (!allowGeocoding || lat === undefined || lng === undefined) return;
        getAddressFromCoordinates(lat, lng).then(addressValue => {
            if (requestVersion === addressRequestVersionRef.current && addressValue) {
                setAddress(addressValue);
            }
        });
    };

    // ============================================================
    // ÉTAT DU MOTEUR DE LECTURE
    // isPlayingRef : SEULS play(), pause(), goLive() le modifient
    // isLiveRef    : true = horloge temps réel pilote la position
    // ============================================================
    const isLiveRef = useRef(true);
    const isPlayingRef = useRef(true);
    const playbackPositionRef = useRef(0);
    const playbackIntervalRef = useRef(null);
    const startPlaybackRef = useRef(null);
    const activateDirectMediaRef = useRef(null);
    const sessionIsActiveRef = useRef(true);
    const lastRenderedStateKeyRef = useRef(null);
    const clockSourceRef = useRef('timeline');
    const playbackAnchorRef = useRef({ position: 0, monotonicMs: 0 });
    const lastTimelineExtensionSecondRef = useRef(-1);
    const directMediaTargetRef = useRef(null);
    const directMediaWaitingRef = useRef(false);
    const directMediaResumePendingRef = useRef(false);
    const replayMediaWaitingRef = useRef(false);

    const monotonicNow = () => (
        typeof performance !== 'undefined' && typeof performance.now === 'function'
            ? performance.now()
            : Date.now()
    );

    const resetTimelineClock = (position) => {
        playbackAnchorRef.current = {
            position: Number(position) || 0,
            monotonicMs: monotonicNow(),
        };
        clockSourceRef.current = 'timeline';
    };

    // ============================================================
    // USEEFFECT — Initialisation annulable (compatible React StrictMode)
    // ============================================================
    useEffect(() => {
        const dom = Object.fromEntries(Object.entries(refs).map(([key, ref]) => [key, ref.current]));
        const generation = ++initializationGenerationRef.current;
        const isStale = () => initializationGenerationRef.current !== generation;

        let bufferCheckInterval = null;

        const handleFullscreenChange = () => {
            // Seul le plein écran de la vidéo agrandit le canvas (la relecture
            // met aussi la carte ou le journal en plein écran).
            const fullscreenElement = document.fullscreenElement || document.webkitFullscreenElement;
            const isFullscreen = !!fullscreenElement && (!dom.canvas || fullscreenElement.contains(dom.canvas));
            sessionMedia.adaptCanvasToFullscreen(dom, isFullscreen);
        };

        const updateDisplayMode = async (newMediaType, newDisplayMode) => {
            console.log(`[UI] 🔄 Changement de mode : ${newMediaType} | Mode: ${newDisplayMode}`);
            sessionContextRef.current.sessionMediaType = newMediaType;
            sessionContextRef.current.currentDisplayMode = newDisplayMode;

            let newViewState = {
                videoSection: false, audioMapSection: false, gpsMapSection: false,
                audioControls: false, toggleVideoVisible: false, toggleAudioVisible: false, toggleText: ''
            };

            try {
                if (isInvestigation) {
                    // Relecture : vidéo (ou son) et carte affichées ensemble, jamais en bascule.
                    newViewState = { ...newViewState, investigation: true, videoSection: newMediaType === 'video', audioControls: newMediaType !== 'gps' };
                    if (sessionMap.initMap('map-container-investigation')) sessionMap.updateMapObjects();
                } else if (newMediaType === 'gps') {
                    newViewState.gpsMapSection = true;
                    if (sessionMap.initMap('map-container-gps')) sessionMap.updateMapObjects();
                } else if (newMediaType === 'audio') {
                    newViewState.audioMapSection = true;
                    if (sessionMap.initMap('map-container-audio')) sessionMap.updateMapObjects();
                } else if (newMediaType === 'video') {
                    if (newDisplayMode === 'video-only') {
                        newViewState.videoSection = true;
                        newViewState.toggleVideoVisible = true;
                        newViewState.toggleText = 'Afficher Audio + Carte';
                    } else {
                        newViewState.audioMapSection = true;
                        newViewState.audioControls = true;
                        newViewState.toggleAudioVisible = true;
                        newViewState.toggleText = 'Afficher Vidéo';
                        if (sessionMap.initMap('map-container-audio')) sessionMap.updateMapObjects();
                    }
                }
            } catch (e) { console.error(e); }

            if (typeof setViewState === 'function') setViewState(newViewState);
            sessionMap.invalidateMapSize();
        };

        const main = async () => {
            console.log("[FRONT] 🚀 Lancement du Viewer Orchéstré...");

            // Secret dans le fragment #k= (jamais envoyé au serveur).
            const access = captureSessionAccess();
            if (!access) {
                return setAccessMessage('Lien incomplet : ouvrez le lien complet reçu par SMS (sa fin, après « #k= », contient la clé de lecture).');
            }
            const linkkey = null;

            const sessionDetails = await sessionData.fetchSessionDetails(sessionId);
            if (isStale()) return;
            if (sessionDetails?.expired) return setAccessMessage(SESSION_EXPIRED);
            if (!sessionDetails?.session) return setAccessMessage('Cette session est introuvable, n’est plus accessible ou le lien est incomplet.');
            if (sessionDetails.session.status === 'archived') {
                return setAccessMessage('Cette session n’est plus accessible.');
            }
            if (!sessionData.sessionPswdKeyRef.current) return setStatusMessage('Clé de déchiffrement non récupérée.');

            const initialData = await sessionData.loadInitialState(sessionId, linkkey);
            if (isStale()) return;
            if (initialData) {
                sessionData.initialStateRef.current = initialData;
                sessionData.timelineCacheRef.current.reset(initialData);
            }

            const lastConvertSessionChange = await sessionData.loadHistoryAndProcess(sessionId, linkkey, isStale);
            if (isStale()) return;

            const latest = sessionData.timelineCacheRef.current.getLatestState();
            const initial = sessionData.timelineCacheRef.current.getStateAtElapsed(0);
            Object.assign(sessionData.virtualSessionRef.current, initial.snapshot, {
                currentLocation: initial.currentLocation,
                victimRoute: initial.positions
            });

            let currentSessionType = latest.snapshot.sessionType || sessionDetails.session.sessionType;
            let shareType = latest.snapshot.shareType || "";
            let currentTrigger = latest.snapshot.trigger || sessionDetails.session.trigger || null;
            let currentAppType = latest.snapshot.appType || sessionDetails.session.appType || null;
            let currentActivityType = latest.snapshot.activityType || null;
            let currentShortcutMode = latest.snapshot.shortcutDetectionMode || null;
            let finalMediaType = getAlertMediaType(currentSessionType, {
                audio: latest.snapshot.audio,
                video: latest.snapshot.video,
                mediaType: sessionDetails.session.mediaType,
            });

            if (lastConvertSessionChange) {
                currentSessionType = lastConvertSessionChange.sessionType;
                shareType = lastConvertSessionChange.shareType;
                currentTrigger = lastConvertSessionChange.trigger || currentTrigger;
                currentAppType = lastConvertSessionChange.appType || currentAppType;
                currentActivityType = lastConvertSessionChange.activityType || currentActivityType;
                currentShortcutMode = lastConvertSessionChange.shortcutDetectionMode || currentShortcutMode;
                finalMediaType = getAlertMediaType(currentSessionType, lastConvertSessionChange);
            }

            sessionContextRef.current.currentSessionType = currentSessionType;
            const initialTimelineConfig = recomputeTimelineRef.current?.(currentSessionType) || {};
            sessionContextRef.current.sessionMediaType = finalMediaType;

            const loc = initial.currentLocation;
            updateAddressFromLocation(loc);

            sessionContextRef.current.travelMode = initial?.selectedTravelMode || initial?.travelMode || null;
            sessionContextRef.current.trailName = initial?.destinationAddressName || null;
            setAlertDetails(getAlertDetails(currentSessionType, currentTrigger, shareType, {
                appType: currentAppType,
                activityType: currentActivityType,
                shortcutDetectionMode: currentShortcutMode,
                travelMode: sessionContextRef.current.travelMode,
                trailName: sessionContextRef.current.trailName,
            }));

            if (isAlertSessionType(currentSessionType) && finalMediaType === 'video') {
                sessionContextRef.current.currentDisplayMode = 'video-only';
            } else if (isAlertSessionType(currentSessionType) && finalMediaType === 'audio') {
                sessionContextRef.current.currentDisplayMode = 'audio-map-combined';
            } else {
                sessionContextRef.current.currentDisplayMode = 'gps-map-only';
                sessionContextRef.current.sessionMediaType = 'gps';
            }

            sessionIsActiveRef.current = sessionDetails?.session?.status === 'active' && !latest.snapshot.isEnded;
            setCanGoLive(sessionIsActiveRef.current);

            // --- Initialisation du média (PAS d'auto-play) ---
            const startMediaAndBuffer = () => {
                if (bufferCheckInterval) clearInterval(bufferCheckInterval);

                setStatusMessage(sessionContextRef.current.sessionMediaType === 'video'
                    ? "Connexion au flux vidéo en cours. Patientez..."
                    : "Connexion au flux audio en cours. Patientez...");

                if (!sessionMedia.hasActiveMedia?.()) {
                    sessionMedia.initializeMedia(dom);
                    sessionMedia.attachVideoEvents(dom, () => mediaClockContextRef.current);
                } else {
                    sessionMedia.hlsRef.current?.startLoad?.();
                }
                sessionMedia.ensureMediaLoading(true);

                // Polling : attend que le HLS ait chargé assez
                bufferCheckInterval = setInterval(() => {
                    const hasSeekableMedia = Boolean(dom.video?.seekable?.length);
                    const hasBufferedMedia = Boolean(dom.video?.buffered?.length);
                    const hasFiniteMedia = Boolean(
                        dom.video &&
                        Number.isFinite(dom.video.duration) &&
                        dom.video.duration > 0
                    );
                    if (hasSeekableMedia || hasBufferedMedia || hasFiniteMedia || (dom.video?.readyState || 0) >= 2) {
                        clearInterval(bufferCheckInterval);
                        bufferCheckInterval = null;
                        if (!directMediaWaitingRef.current) setStatusMessage(null);
                    } else {
                        sessionMedia.ensureMediaLoading();
                    }
                }, 1000);
            };

            if (sessionContextRef.current.sessionMediaType === 'audio' || sessionContextRef.current.sessionMediaType === 'video') {
                startMediaAndBuffer();
            }

            // Callback CONVERTSESSION temps réel
            const onConvertSessionRealtime = (change) => {
                console.log("[FRONT] ⚡ CONVERTSESSION reçu en temps réel:", change);
                const nextType = change.sessionType;
                const nextTrigger = change.trigger;
                const nextShareType = change.shareType || "";
                let nextMedia = getAlertMediaType(nextType, change);
                let nextMode = 'gps-map-only';

                const wasLive = isLiveRef.current;
                sessionContextRef.current.currentSessionType = nextType;
                const timelineConfig = recomputeTimelineRef.current?.(nextType) || {};
                setAlertDetails(getAlertDetails(nextType, nextTrigger, nextShareType, {
                    appType: change.appType || sessionDetails.session.appType,
                    activityType: change.activityType,
                    shortcutDetectionMode: change.shortcutDetectionMode,
                    travelMode: sessionContextRef.current.travelMode,
                    trailName: sessionContextRef.current.trailName,
                }));

                if (isAlertSessionType(nextType) && nextMedia === 'video') nextMode = 'video-only';
                else if (isAlertSessionType(nextType) && nextMedia === 'audio') nextMode = 'audio-map-combined';

                const previousMedia = sessionContextRef.current.sessionMediaType;
                sessionContextRef.current.sessionMediaType = nextMedia;
                sessionContextRef.current.currentDisplayMode = nextMode;
                if ((nextMedia === 'audio' || nextMedia === 'video') && previousMedia === 'gps') {
                    startMediaAndBuffer();
                }

                updateDisplayMode(nextMedia, nextMode);

                if (wasLive && isAlertSessionType(nextType) && (nextMedia === 'audio' || nextMedia === 'video')) {
                    activateDirectMediaRef.current?.({
                        position: timelineConfig.duration || 0,
                        preferLiveBuffer: false,
                    });
                }
            };

            await updateDisplayMode(sessionContextRef.current.sessionMediaType, sessionContextRef.current.currentDisplayMode);
            if (isStale()) return;

            const checkHistoricalMode = (sessionInfo, applySessionState = true) => {
                const s = sessionInfo || sessionData.virtualSessionRef.current;
                const status = sessionDetails.session.status;
                const isEnded = s.isEnded || status === 'stopped' || status === 'ended' || status === 'END' || s.status === 'END';
                const responders = Object.values(s.responders || {});
                const allRespondersDone = responders.length === 0 || responders.every(r => r.status === 'completed' || r.status === 'left' || r.status === 'END' || (r.lat === 0 && r.lng === 0));
                
                const histMode = isEnded && allRespondersDone;
                if (isEnded) {
                    sessionIsActiveRef.current = false;
                    setCanGoLive(false);
                }
                setIsHistoricalMode(histMode);
                if (applySessionState) setSessionStateIfChanged(s);
            };

            checkHistoricalMode(latest.snapshot, false);

            // Tant que l'utilisateur n'a pas explicitement choisi le Direct,
            // les arrivées MQTT ne doivent pas remplacer le snapshot initial.
            isLiveRef.current = false;
            isPlayingRef.current = false;
            playbackPositionRef.current = 0;

            // ─────────────────────────────────────────────────────
            // REALTIME HANDLER
            // ─────────────────────────────────────────────────────
            const handleRealtimeUpdate = () => {
                recomputeTimelineRef.current?.(
                    null,
                    isLiveRef.current ? playbackPositionRef.current : 0
                );

                // Appliquer à la vue uniquement si l'utilisateur est en DIRECT
                if (isLiveRef.current) {
                    if (doesDirectMediaOwnClock()) {
                        // Le média visible peut être en retard sur le dernier
                        // événement reçu : conserver toutes les données au même T.
                        rewindMapToPosition(playbackPositionRef.current);
                    } else {
                        const latest = sessionData.timelineCacheRef.current.getLatestState();
                        Object.assign(sessionData.virtualSessionRef.current, latest.snapshot, {
                            currentLocation: latest.currentLocation,
                            victimRoute: latest.positions
                        });

                        sessionMap.updateMapObjectsAtSnapshot(latest.snapshot, latest.positions, true);
                        lastRenderedStateKeyRef.current = `event:${sessionData.timelineCacheRef.current.eventTimestamps.length}:${sessionData.timelineCacheRef.current.eventTimestamps.at(-1) || 0}`;

                        updateAddressFromLocation(latest.currentLocation, false);

                        checkHistoricalMode(latest.snapshot);
                        setSessionStateIfChanged(latest.snapshot);
                    }
                }
            };

            if (sessionIsActiveRef.current) {
                try {
                    await sessionData.startRealtimeSync(
                        sessionId,
                        linkkey,
                        handleRealtimeUpdate,
                        onConvertSessionRealtime,
                        isStale
                    );
                    if (isStale()) {
                        return;
                    }
                } catch (error) {
                    console.error("[MQTT VIEWER] Initialisation impossible");
                    setStatusMessage("Le suivi en temps réel est temporairement indisponible.");
                }
            }

            // Détection du mode live ou historique
            const isSessionActuallyEnded = latest.snapshot.isEnded || sessionDetails.session.status === 'stopped' || sessionDetails.session.status === 'ended';
            // Test diagnostic : en historique, démarrer directement sur le
            // tout premier segment RÉEL (celui du fichier m3u8, connu
            // seulement une fois reçu et décodé) plutôt que sur le
            // timestamp de la bascule en alerte (qui précède souvent le
            // vrai début de la capture de quelques secondes).
            const hasMediaZone = sessionContextRef.current.sessionMediaType === 'audio'
                || sessionContextRef.current.sessionMediaType === 'video';

            // L'affichage naît toujours du snapshot initial déchiffré, sans
            // événement. Pour une session active, le clic « Continuer » appelle
            // ensuite onGoLive et bascule explicitement vers le Direct.
            isLiveRef.current = false;
            isPlayingRef.current = isSessionActuallyEnded;
            playbackPositionRef.current = 0;
            resetTimelineClock(0);
            Object.assign(sessionData.virtualSessionRef.current, initial.snapshot, {
                currentLocation: initial.currentLocation,
                victimRoute: initial.positions
            });
            sessionMap.updateMapObjectsAtSnapshot(initial.snapshot, initial.positions, true);
            lastRenderedStateKeyRef.current = 'initial';
            setSessionStateIfChanged(initial.snapshot);
            sessionMedia.setTimelineState(prev => ({
                ...prev,
                value: 0,
                isLive: false,
                display: prev.max > 0 ? `⏱ ${fmtTimelineClock(0)} / ${fmtTimelineClock(prev.max)}` : `⏱ Chargement…`
            }));

            if (isSessionActuallyEnded && hasMediaZone) {
                // Attend le tout premier segment réellement décodé (jusqu'à
                // 8 s), pour connaître son timestamp réel via l'ID3 embarqué.
                const gotFirstSegment = await new Promise(resolve => {
                    if ((sessionMedia.id3CuesTimelineRef.current?.length || 0) > 0) return resolve(true);
                    const startedAt = Date.now();
                    const poll = setInterval(() => {
                        if (isStale()) { clearInterval(poll); resolve(false); return; }
                        if ((sessionMedia.id3CuesTimelineRef.current?.length || 0) > 0) {
                            clearInterval(poll);
                            resolve(true);
                        } else if (Date.now() - startedAt > 8000) {
                            clearInterval(poll);
                            resolve(false);
                        }
                    }, 200);
                });
                if (isStale()) return;

                if (gotFirstSegment) {
                    const firstCue = sessionMedia.id3CuesTimelineRef.current[0];
                    const realStartPosition = sessionMedia.getSessionElapsedAtMediaTime(
                        firstCue.pts,
                        getSessionStartTimestamp(),
                        getMediaOffset()
                    );
                    if (Number.isFinite(realStartPosition) && realStartPosition >= 0) {
                        const realStartState = sessionData.timelineCacheRef.current.getStateAtElapsed(realStartPosition);
                        playbackPositionRef.current = realStartPosition;
                        resetTimelineClock(realStartPosition);
                        Object.assign(sessionData.virtualSessionRef.current, realStartState.snapshot, {
                            currentLocation: realStartState.currentLocation,
                            victimRoute: realStartState.positions
                        });
                        sessionMap.updateMapObjectsAtSnapshot(realStartState.snapshot, realStartState.positions, true);
                        lastRenderedStateKeyRef.current = null;
                        setSessionStateIfChanged(realStartState.snapshot);
                        updateAddressFromLocation(realStartState.currentLocation);
                        sessionMedia.setTimelineState(prev => ({
                            ...prev,
                            value: realStartPosition,
                            isLive: false,
                            display: `⏱ ${fmtTimelineClock(realStartPosition)} / ${fmtTimelineClock(prev.max)}`
                        }));
                    }
                }
            }

            startPlaybackRef.current?.();

            if (dom.toggleViewButton) {
                dom.toggleViewButton.onclick = () => {
                    if (sessionContextRef.current.sessionMediaType === 'video') {
                        const nextMode = (sessionContextRef.current.currentDisplayMode === 'video-only') ? 'audio-map-combined' : 'video-only';
                        updateDisplayMode(sessionContextRef.current.sessionMediaType, nextMode);
                    }
                };
            }
        };

        document.addEventListener("fullscreenchange", handleFullscreenChange);
        document.addEventListener("webkitfullscreenchange", handleFullscreenChange);
        console.log("[FRONT] ✅ Système prêt.");
        main().catch(() => {
            if (!isStale()) setStatusMessage('Le suivi de session n’a pas pu être initialisé.');
        });

        return () => {
            if (initializationGenerationRef.current === generation) {
                initializationGenerationRef.current += 1;
            }
            stopPlaybackInterval();
            directMediaTargetRef.current = null;
            directMediaWaitingRef.current = false;
            replayMediaWaitingRef.current = false;
            sessionMedia.cleanupMedia();
            sessionData.stopRealtimeSync();
            sessionMap.cleanupMap();
            if (bufferCheckInterval) clearInterval(bufferCheckInterval);
            document.removeEventListener("fullscreenchange", handleFullscreenChange);
            document.removeEventListener("webkitfullscreenchange", handleFullscreenChange);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // ============================================================
    // TIMELINE — recalcul des segments (gradient, mediaOffset)
    // NE change PAS la position du curseur.
    // ============================================================
    const recomputeTimeline = (overrideType = null, minimumElapsed = 0) => {
        const cachedTimelineData = sessionData.timelineCacheRef.current.getTimelineData();
        const requestedMaxTs = cachedTimelineData.minTs + Math.max(0, Number(minimumElapsed) || 0) * 1000;
        const extendedMaxTs = Math.max(cachedTimelineData.maxTs, requestedMaxTs);
        const timelineData = extendedMaxTs > cachedTimelineData.maxTs
            ? {
                ...cachedTimelineData,
                maxTs: extendedMaxTs,
                segments: cachedTimelineData.segments.map((segment, index, segments) => (
                    index === segments.length - 1
                        ? { ...segment, endTs: extendedMaxTs }
                        : segment
                )),
            }
            : cachedTimelineData;
        const initialType = sessionData.timelineCacheRef.current.initialSnapshot?.sessionType || overrideType;
        const timelineConfig = computeTimelineSegments(timelineData, initialType);
        mediaClockContextRef.current = {
            sessionStartTs: timelineConfig.minTs || sessionData.timelineCacheRef.current.originTimestamp || 0,
            fallbackOffset: timelineConfig.mediaOffsetSeconds || 0,
        };
        sessionMedia.setTimelineState(prev => ({
            ...prev,
            max: timelineConfig.duration,
            gradient: timelineConfig.gradient,
            minTs: timelineConfig.minTs,
            maxTs: timelineConfig.maxTs,
            mediaOffset: timelineConfig.mediaOffsetSeconds
        }));
        sessionMedia.refreshMediaProgress();
        if (isInvestigation) {
            const cache = sessionData.timelineCacheRef.current;
            setJournal(buildSessionJournal(cache.events, cache.initialSnapshot));
            setTimelineMarkers(buildTimelineMarkers(cache.events, cache.initialSnapshot));
        }
        return timelineConfig;
    };

    recomputeTimelineRef.current = recomputeTimeline;
    const timelineStateRef = useRef(sessionMedia.timelineState);
    timelineStateRef.current = sessionMedia.timelineState;

    // ============================================================
    // UTILITAIRES
    // ============================================================
    const domRefWrapper = Object.fromEntries(Object.entries(refs).map(([key, ref]) => [key, ref.current]));

    const fmtTime = (totalSec) => {
        const s = Math.floor(totalSec || 0);
        const m = Math.floor(s / 60);
        const ss = s % 60;
        return `${String(m).padStart(2, '0')}:${String(ss).padStart(2, '0')}`;
    };

    const fmtTimelineClock = (elapsedSec) => {
        const origin = getSessionStartTimestamp();
        if (!origin) return fmtTime(elapsedSec);
        return new Date(origin + Math.max(0, Number(elapsedSec) || 0) * 1000)
            .toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit', second: '2-digit' });
    };

    /** mediaOffset est FIXE — déterminé par le timestamp du CONVERTSESSION */
    const getMediaOffset = () => {
        return timelineStateRef.current.mediaOffset || 0;
    };

    const getSessionStartTimestamp = () => (
        timelineStateRef.current.minTs || sessionData.timelineCacheRef.current.originTimestamp || 0
    );

    const getMediaClockPosition = () => {
        const video = domRefWrapper.video;
        if (!video || !Number.isFinite(video.currentTime)) return null;
        return sessionMedia.getSessionElapsedAtMediaTime(
            video.currentTime,
            getSessionStartTimestamp(),
            getMediaOffset()
        );
    };

    const isAlertMediaAtPosition = position => {
        const snapshot = sessionData.timelineCacheRef.current.getStateAtElapsed(position).snapshot;
        return Boolean(
            isAlertSessionType(snapshot?.sessionType)
            && (snapshot?.audio || snapshot?.video)
            && (sessionContextRef.current.sessionMediaType === 'audio'
                || sessionContextRef.current.sessionMediaType === 'video')
            && (sessionMedia.hasActiveMedia?.() || sessionMedia.hlsRef?.current || domRefWrapper.video?.src)
            && domRefWrapper.video
        );
    };

    // En Direct, la propriete de l'horloge ne depend jamais de video.paused.
    // Une pause due au buffer doit figer la carte et le curseur, pas rendre la
    // main a l'horloge murale.
    const doesDirectMediaOwnClock = () => (
        isLiveRef.current && isAlertMediaAtPosition(playbackPositionRef.current)
    );

    const setDirectMediaWaiting = (waiting, bufferedAhead = 0, requiredBuffer = 0) => {
        directMediaWaitingRef.current = waiting;
        if (!waiting) directMediaResumePendingRef.current = false;
        const message = waiting
            ? `Connexion au média… (${Math.floor(bufferedAhead)}/${Math.ceil(requiredBuffer)} s)`
            : null;
        setStatusMessage(message);
        sessionMedia.setSlaveState(prev => (
            prev.display === message ? prev : { ...prev, display: message }
        ));
    };

    const setReplayMediaWaiting = (waiting, bufferedAhead = 0, requiredBuffer = 0) => {
        if (replayMediaWaitingRef.current === waiting && !waiting) return;
        replayMediaWaitingRef.current = waiting;
        const message = waiting
            ? `Chargement du média… (${Math.floor(bufferedAhead)}/${Math.ceil(requiredBuffer)} s)`
            : null;
        setStatusMessage(message);
        sessionMedia.setSlaveState(prev => (
            prev.display === message ? prev : { ...prev, display: message }
        ));
    };

    const getReplayMediaBufferState = position => {
        if (!isAlertMediaAtPosition(position)) return { shouldWait: false };
        const video = domRefWrapper.video;
        const mediaTime = sessionMedia.getMediaTimeAtSessionElapsed(
            position,
            getSessionStartTimestamp(),
            getMediaOffset()
        );
        if (!Number.isFinite(mediaTime) || mediaTime < 0) {
            return { shouldWait: false };
        }

        const seekableStart = video.seekable?.length
            ? video.seekable.start(0)
            : 0;
        const playableMediaTime = Math.max(mediaTime, seekableStart);
        const duration = Number(video.duration);
        const remaining = Number.isFinite(duration)
            ? Math.max(0, duration - playableMediaTime)
            : Infinity;
        if (remaining <= 0.15) return { shouldWait: false };

        const bufferedAhead = sessionMedia.getBufferedAhead(playableMediaTime, video);
        const isCurrentlyWaiting = replayMediaWaitingRef.current || video.paused;
        const threshold = isCurrentlyWaiting ? Math.min(0.8, remaining) : 0.1;
        const shouldWait = bufferedAhead < threshold;

        return {
            shouldWait,
            bufferedAhead,
            requiredBuffer: isCurrentlyWaiting ? threshold : 0.8,
            mediaTime: playableMediaTime,
        };
    };

    const setSessionStateIfChanged = (newVs) => {
        if (!newVs) return;
        setSessionState(prev => {
            if (!prev) return { ...newVs };
            if (
                prev.sessionType === newVs.sessionType &&
                prev.trigger === newVs.trigger &&
                prev.travelMode === newVs.travelMode &&
                prev.isCorridorEnabled === newVs.isCorridorEnabled &&
                prev.corridorSizeMeters === newVs.corridorSizeMeters &&
                prev.stopDetectionDurationMs === newVs.stopDetectionDurationMs &&
                prev.isStopDetectionEnabled === newVs.isStopDetectionEnabled &&
                prev.isStopped === newVs.isStopped &&
                prev.stopTimerValue === newVs.stopTimerValue &&
                prev.audio === newVs.audio &&
                prev.video === newVs.video &&
                prev.isAccidentDetectionEnabled === newVs.isAccidentDetectionEnabled &&
                prev.isShakeDetectionEnabled === newVs.isShakeDetectionEnabled &&
                prev.isShortcutDetectionEnabled === newVs.isShortcutDetectionEnabled &&
                prev.shortcutDetectionMode === newVs.shortcutDetectionMode &&
                prev.sportPauseUntil === newVs.sportPauseUntil &&
                prev.trailApproachName === newVs.trailApproachName &&
                prev.sportCheck === newVs.sportCheck
            ) {
                return prev;
            }
            return { ...newVs };
        });
    };

    // ============================================================
    // REWIND MAP — recherche instantanée O(log N) dans le cache
    // ============================================================
    const rewindMapToPosition = (secondsSinceStart, force = false) => {
        const { snapshot, positions, currentLocation, stateKey } = sessionData.timelineCacheRef.current.getStateAtElapsed(secondsSinceStart);
        if (!force && stateKey === lastRenderedStateKeyRef.current) return;
        lastRenderedStateKeyRef.current = stateKey;

        // Copier l'état dans virtualSessionRef
        Object.assign(sessionData.virtualSessionRef.current, snapshot, {
            currentLocation,
            victimRoute: positions
        });

        // Mise à jour de la carte en O(1) sans recréation de couches DOM
        sessionMap.updateMapObjectsAtSnapshot(snapshot, positions, true);

        // Mettre à jour l'état React sans re-rendus inutiles
        setSessionStateIfChanged(snapshot);

        updateAddressFromLocation(currentLocation);
    };

    // ============================================================
    // SYNC MEDIA — Fonction centrale
    // Appelée APRÈS chaque changement de position.
    // Le lecteur HLS ne décide JAMAIS par lui-même.
    // mediaOffset est FIXE → pas d'oscillation → pas de replay.
    // ============================================================
    const syncMedia = (position, forceSeek = false, allowPlayback = isPlayingRef.current) => {
        const video = domRefWrapper.video;
        const hasMedia = !!((sessionMedia.hasActiveMedia?.() || sessionMedia.hlsRef?.current || video?.src) && video);
        if (!hasMedia) return;

        const mediaSnapshot = sessionData.timelineCacheRef.current.getStateAtElapsed(position).snapshot;
        const isAlertMedia = isAlertSessionType(mediaSnapshot?.sessionType)
            && Boolean(mediaSnapshot?.audio || mediaSnapshot?.video);

        if (sessionContextRef.current.sessionMediaType === 'gps' || !isAlertMedia) {
            sessionMedia.setMediaFrameVisible(false);
            if (!video.paused) video.pause();
            return;
        }

        const mo = getMediaOffset();
        const finiteDuration = Number.isFinite(video.duration) ? video.duration : 0;
        const seekableStart = video.seekable?.length
            ? video.seekable.start(0)
            : 0;
        const seekableEnd = video.seekable?.length
            ? video.seekable.end(video.seekable.length - 1)
            : 0;
        const bufferedEnd = video.buffered?.length
            ? video.buffered.end(video.buffered.length - 1)
            : 0;
        const videoDuration = Math.max(finiteDuration, seekableEnd, bufferedEnd);

        const mediaTime = sessionMedia.getMediaTimeAtSessionElapsed(
            position,
            getSessionStartTimestamp(),
            mo
        );

        if (!Number.isFinite(mediaTime) || mediaTime < 0 || videoDuration <= 0) {
            // HORS zone média → pause technique et effacement du canvas
            sessionMedia.setMediaFrameVisible(false);
            if (!video.paused) video.pause();
            sessionMedia.setSlaveState(prev => ({
                ...prev,
                position: 0,
                display: mo > 0 ? `⏳ Média à ${fmtTime(mo)}` : `⏳ Chargement…`
            }));
            return;
        }

        // Après la fin du clip → on stoppe la vidéo si la session est terminée
        if (isHistoricalMode && mediaTime > videoDuration) {
            sessionMedia.setMediaFrameVisible(false);
            if (!video.paused) video.pause();
            return;
        }

        const clampedTime = Math.max(seekableStart, Math.min(mediaTime, videoDuration));
        sessionMedia.setMediaFrameVisible(true);

        // Si seek forcé (touché par l'utilisateur) ou drift excessif. Dès que
        // le média joue, son horloge native prend le relais et on ne le seek
        // plus à chaque tick : la vidéo reste fluide.
        const drift = Math.abs(video.currentTime - clampedTime);
        if (forceSeek || (video.paused && drift > 0.3) || drift > 2.0) {
            try {
                video.currentTime = clampedTime;
            } catch (_) {}
        }

        // Play/Pause : géré par isPlayingRef
        if (allowPlayback) {
            if (video.paused) video.play().catch(() => { });
        } else {
            if (!video.paused) video.pause();
        }

        sessionMedia.setSlaveState(prev => ({
            ...prev,
            position: Math.floor(video.currentTime),
            display: null
        }));
    };

    const syncMediaRef = useRef(syncMedia);
    syncMediaRef.current = syncMedia;

    // ============================================================
    // MOTEUR DE LECTURE UNIFIÉ
    // Gère les DEUX modes : replay ET direct
    // - le temps monotone pilote en l'absence de média ;
    // - l'horloge native HLS pilote dès que le média joue ;
    // - carte, événements et curseur sont tous relus au même instant T.
    // ============================================================
    const stopPlaybackInterval = () => {
        if (playbackIntervalRef.current) {
            clearInterval(playbackIntervalRef.current);
            playbackIntervalRef.current = null;
        }
    };

    const activateDirectMedia = ({ position, preferLiveBuffer = false }) => {
        const video = domRefWrapper.video;
        if (!video || (!sessionMedia.hasActiveMedia?.() && !sessionMedia.hlsRef?.current && !video.src)) return false;

        let targetPosition = Math.max(0, Number(position) || 0);
        let targetMediaTime = sessionMedia.getMediaTimeAtSessionElapsed(
            targetPosition,
            getSessionStartTimestamp(),
            getMediaOffset()
        );

        if (preferLiveBuffer) {
            const safeMediaTime = sessionMedia.getSafeDirectMediaTime(video);
            if (Number.isFinite(safeMediaTime)) {
                targetMediaTime = safeMediaTime;
                targetPosition = sessionMedia.getSessionElapsedAtMediaTime(
                    safeMediaTime,
                    getSessionStartTimestamp(),
                    getMediaOffset()
                );
            }
        }

        isLiveRef.current = true;
        isPlayingRef.current = true;
        clockSourceRef.current = 'media';
        directMediaTargetRef.current = targetPosition;
        playbackPositionRef.current = targetPosition;

        if (!video.paused) video.pause();
        if (Number.isFinite(targetMediaTime) && targetMediaTime >= 0) {
            try {
                video.currentTime = targetMediaTime;
            } catch (_) {
                // Le seek sera retente au prochain tick, apres les metadonnees.
            }
        }

        sessionMedia.setMediaFrameVisible(true);
        sessionMedia.ensureMediaLoading(true);
        setDirectMediaWaiting(true, 0, sessionMedia.getRequiredDirectBufferSeconds());
        directMediaResumePendingRef.current = false;
        recomputeTimelineRef.current?.(null, targetPosition);
        rewindMapToPosition(targetPosition, true);
        sessionMedia.setTimelineState(prev => ({
            ...prev,
            value: targetPosition,
            isLive: true,
            display: `⏱ 🔴 Direct (${fmtTimelineClock(targetPosition)})`
        }));
        return true;
    };

    const startPlaybackInterval = () => {
        stopPlaybackInterval();
        playbackIntervalRef.current = setInterval(() => {
            if (!isPlayingRef.current) return;

            const { minTs, max } = timelineStateRef.current;
            if (!minTs) return;

            const lastPos = playbackPositionRef.current;
            const eventsMax = max || 0;
            const directMediaOwnsClock = doesDirectMediaOwnClock();
            let mediaClockRunning = false;
            let mediaPosition = null;
            let timelinePosition = lastPos;

            if (directMediaOwnsClock) {
                const video = domRefWrapper.video;
                const requiredBuffer = sessionMedia.getRequiredDirectBufferSeconds();

                if (Number.isFinite(directMediaTargetRef.current)) {
                    let requestedMediaTime = sessionMedia.getMediaTimeAtSessionElapsed(
                        directMediaTargetRef.current,
                        getSessionStartTimestamp(),
                        getMediaOffset()
                    );
                    // Négatif + premier segment déjà connu = la cible visée est
                    // avant le vrai début de la capture (démarrage caméra/micro
                    // retardé après le début de l'alerte) : rien n'y arrivera
                    if (Number.isFinite(requestedMediaTime) && requestedMediaTime < 0) {
                        requestedMediaTime = 0;
                    }
                    if (Number.isFinite(requestedMediaTime)
                        && requestedMediaTime >= 0
                        && Math.abs(video.currentTime - requestedMediaTime) > 0.2) {
                        try {
                            video.currentTime = requestedMediaTime;
                        } catch (_) {
                            // Les metadonnees HLS ne sont pas encore disponibles.
                        }
                    }
                }

                const bufferedAhead = sessionMedia.getBufferedAhead(video.currentTime, video);
                const isCurrentlyWaiting = directMediaWaitingRef.current || video.paused;
                const isMediaReady = bufferedAhead >= 0.5 || video.readyState >= 2 || (video.seekable?.length || 0) > 0;

                if (isCurrentlyWaiting) {
                    if (isMediaReady) {
                        if (video.muted) video.muted = false;
                        sessionMedia.resumeAudio();
                        if (!directMediaResumePendingRef.current) {
                            directMediaResumePendingRef.current = true;
                            video.play().then(() => {
                                directMediaTargetRef.current = null;
                                setDirectMediaWaiting(false);
                            }).catch(() => {
                                directMediaResumePendingRef.current = false;
                                setDirectMediaWaiting(true, bufferedAhead, requiredBuffer);
                            });
                        }
                    } else {
                        directMediaResumePendingRef.current = false;
                        if (!video.paused) video.pause();
                        setDirectMediaWaiting(true, bufferedAhead, requiredBuffer);
                        sessionMedia.ensureMediaLoading();
                    }
                } else if (bufferedAhead <= 0.15 && video.readyState < 2) {
                    video.pause();
                    setDirectMediaWaiting(true, bufferedAhead, requiredBuffer);
                    sessionMedia.ensureMediaLoading();
                }

                mediaPosition = directMediaWaitingRef.current
                    ? (directMediaTargetRef.current ?? lastPos)
                    : getMediaClockPosition();
                mediaClockRunning = Number.isFinite(mediaPosition);
                clockSourceRef.current = 'media';
            } else if (isLiveRef.current) {
                clockSourceRef.current = 'live';
            } else {
                if (clockSourceRef.current !== 'timeline') {
                    resetTimelineClock(lastPos);
                }
                const anchor = playbackAnchorRef.current;
                timelinePosition = anchor.position + (monotonicNow() - anchor.monotonicMs) / 1000;
            }

            let nextPos = selectSharedClockPosition({
                mediaClockRunning,
                mediaPosition,
                isLive: isLiveRef.current,
                wallClockPosition: Math.max(0, (Date.now() - minTs) / 1000),
                eventsMax,
                timelinePosition,
            });

            if (!Number.isFinite(nextPos)) return;
            nextPos = Math.max(0, nextPos);

            // En replay, ne jamais laisser la timeline dépasser le début du
            // média pendant que les segments correspondants sont encore en
            // chargement. Le replay reste maître, mais son horloge est figée
            // jusqu'à disposer d'une petite réserve de lecture.
            let replayMediaShouldWait = false;
            if (!isLiveRef.current) {
                const replayBuffer = getReplayMediaBufferState(nextPos);
                replayMediaShouldWait = replayBuffer.shouldWait === true;
                if (replayMediaShouldWait) {
                    const video = domRefWrapper.video;
                    if (video && !video.paused) video.pause();
                    if (video && Number.isFinite(replayBuffer.mediaTime)) {
                        try {
                            if (Math.abs(video.currentTime - replayBuffer.mediaTime) > 0.2) {
                                video.currentTime = replayBuffer.mediaTime;
                            }
                        } catch (_) {
                            // Le seek sera retente lorsque les métadonnées seront disponibles.
                        }
                    }
                    sessionMedia.ensureMediaLoading();
                    setReplayMediaWaiting(
                        true,
                        replayBuffer.bufferedAhead,
                        replayBuffer.requiredBuffer
                    );
                    nextPos = lastPos;
                    resetTimelineClock(lastPos);
                } else if (replayMediaWaitingRef.current) {
                    setReplayMediaWaiting(false);
                }
            }

            // Une session en direct continue même entre deux événements. Étendre
            // la dernière couleur sans inventer de nouvel événement.
            if (isLiveRef.current && nextPos > eventsMax) {
                const extensionSecond = Math.floor(nextPos);
                if (extensionSecond > lastTimelineExtensionSecondRef.current) {
                    lastTimelineExtensionSecondRef.current = extensionSecond;
                    recomputeTimelineRef.current?.(null, nextPos);
                }
            }

            // Une seule lecture du cache alimente toutes les données visuelles.
            rewindMapToPosition(nextPos);
            playbackPositionRef.current = nextPos;

            // En replay, la timeline reste maitresse et le media la suit. En
            // Direct media, aucun seek periodique ne perturbe la lecture native.
            if (!directMediaOwnsClock) {
                if (replayMediaShouldWait) {
                    sessionMedia.setMediaFrameVisible(false);
                } else {
                    syncMedia(nextPos, false, isPlayingRef.current);
                }
            }

            if (isLiveRef.current) {
                sessionMedia.setTimelineState(prev => ({
                    ...prev,
                    max: Math.max(prev.max, nextPos),
                    value: nextPos,
                    isLive: true,
                    display: `⏱ 🔴 Direct (${fmtTimelineClock(nextPos)})`
                }));
            } else {
                sessionMedia.setTimelineState(prev => ({
                    ...prev,
                    value: nextPos,
                    isLive: false,
                    display: `⏱ ${fmtTimelineClock(nextPos)} / ${fmtTimelineClock(prev.max)}`
                }));
            }

        }, 100);
    };

    // Exposer startPlaybackInterval au useEffect via ref
    startPlaybackRef.current = startPlaybackInterval;
    activateDirectMediaRef.current = activateDirectMedia;

    // ============================================================
    // onTimeSeek — appelé pendant et après un drag de la timeline
    // NE modifie JAMAIS isPlayingRef
    // ============================================================
    domRefWrapper.onTimeSeek = (secondsSinceStart, isFinalChange) => {
        const { minTs } = timelineStateRef.current;
        if (!minTs) return;

        const sec = Number(secondsSinceStart);
        rewindMapToPosition(sec, sec <= 0);

        if (isFinalChange) {
            syncMedia(sec, true, false);
        } else {
            const video = domRefWrapper.video;
            if (video && !video.paused) video.pause();
        }
    };

    const baseMediaHandlers = sessionMedia.getMediaHandlers(domRefWrapper, setStatusMessage);

    // ============================================================
    // HANDLERS — Chaque action modifie l'état, puis syncMedia
    // ============================================================
    const customMediaHandlers = {
        ...baseMediaHandlers,

        // ═══════════════════════════════════
        // PLAY : isPlayingRef → true
        // ═══════════════════════════════════
        onPlay: () => {
            isPlayingRef.current = true;
            setReplayMediaWaiting(false);
            setStatusMessage(null);
            const video = domRefWrapper.video;
            if (video?.muted) video.muted = false;
            sessionMedia.resumeAudio();
            if (!isLiveRef.current) {
                resetTimelineClock(playbackPositionRef.current);
                syncMedia(playbackPositionRef.current);
            }
            startPlaybackInterval(); // Le moteur gère replay ET direct
        },

        // ═══════════════════════════════════
        // PAUSE : isPlayingRef → false
        // ═══════════════════════════════════
        onPause: () => {
            isPlayingRef.current = false;
            isLiveRef.current = false;
            directMediaTargetRef.current = null;
            directMediaWaitingRef.current = false;
            setReplayMediaWaiting(false);
            setStatusMessage(null);
            stopPlaybackInterval();

            syncMedia(playbackPositionRef.current, false, false);

            sessionMedia.setTimelineState(prev => ({
                ...prev,
                value: playbackPositionRef.current,
                display: `⏱ ${fmtTimelineClock(playbackPositionRef.current)} / ${fmtTimelineClock(prev.max)}`
            }));
        },

        // ═══════════════════════════════════
        // DÉBUT : curseur → 0, isPlayingRef INCHANGÉ
        // ═══════════════════════════════════
        onStart: () => {
            isLiveRef.current = false;
            directMediaTargetRef.current = null;
            directMediaWaitingRef.current = false;
            setReplayMediaWaiting(false);
            setStatusMessage(null);
            stopPlaybackInterval();
            const video = domRefWrapper.video;
            if (video && !video.paused) video.pause();
            playbackPositionRef.current = 0;
            resetTimelineClock(0);
            rewindMapToPosition(0, true);
            syncMedia(0);

            sessionMedia.setTimelineState(prev => ({
                ...prev,
                value: 0,
                display: `⏱ ${fmtTimelineClock(0)} / ${fmtTimelineClock(prev.max)}`
            }));

            if (isPlayingRef.current) startPlaybackInterval();
        },

        // ═══════════════════════════════════
        // DIRECT : isPlayingRef → true (forcé)
        // ═══════════════════════════════════
        onGoLive: () => {
            if (!sessionIsActiveRef.current) return;
            const videoEl = domRefWrapper.video;
            if (videoEl && videoEl.muted) videoEl.muted = false;
            sessionMedia.resumeAudio();
            setReplayMediaWaiting(false);

            stopPlaybackInterval();

            const latest = sessionData.timelineCacheRef.current.getLatestState();
            Object.assign(sessionData.virtualSessionRef.current, latest.snapshot, {
                currentLocation: latest.currentLocation,
                victimRoute: latest.positions
            });

            sessionMap.updateMapObjectsAtSnapshot(latest.snapshot, latest.positions, true);
            lastRenderedStateKeyRef.current = null;
            setSessionStateIfChanged(latest.snapshot);

            const timelineConfig = recomputeTimelineRef.current?.() || {};
            const latestHasMedia = isAlertSessionType(latest.snapshot?.sessionType)
                && Boolean(latest.snapshot?.audio || latest.snapshot?.video)
                && (sessionContextRef.current.sessionMediaType === 'audio'
                    || sessionContextRef.current.sessionMediaType === 'video');

            if (latestHasMedia && activateDirectMedia({
                // Le bouton Direct doit rejoindre la fin du temps partagé.
                // mediaOffsetSeconds désigne le début du média dans la timeline,
                // pas sa position live.
                position: timelineConfig.duration || 0,
                preferLiveBuffer: false,
            })) {
                updateAddressFromLocation(latest.currentLocation, false);
                return;
            }

            isLiveRef.current = true;
            isPlayingRef.current = true;
            directMediaTargetRef.current = null;
            directMediaWaitingRef.current = false;
            setStatusMessage(null);
            clockSourceRef.current = 'live';
            const wallClockPosition = timelineConfig.minTs
                ? Math.max(0, (Date.now() - timelineConfig.minTs) / 1000)
                : 0;
            const livePos = Math.max(timelineConfig.duration || 0, wallClockPosition);
            playbackPositionRef.current = livePos;
            recomputeTimelineRef.current?.(null, livePos);
            rewindMapToPosition(livePos, true);

            sessionMedia.setTimelineState(prev => ({
                ...prev,
                max: Math.max(prev.max, livePos),
                value: livePos,
                isLive: true,
                display: `⏱ 🔴 Direct (${fmtTimelineClock(livePos)})`
            }));

            startPlaybackInterval();

            updateAddressFromLocation(latest.currentLocation, false);
        },

        // ═══════════════════════════════════
        // DRAG (pendant) : isPlayingRef INCHANGÉ
        // ═══════════════════════════════════
        onTimelineInput: (val) => {
            stopPlaybackInterval();
            isLiveRef.current = false;
            directMediaTargetRef.current = null;
            directMediaWaitingRef.current = false;
            setReplayMediaWaiting(false);
            setStatusMessage(null);

            const mediaSnapshot = sessionData.timelineCacheRef.current.getStateAtElapsed(Number(val)).snapshot;
            const isAlertMedia = isAlertSessionType(mediaSnapshot?.sessionType)
                && Boolean(mediaSnapshot?.audio || mediaSnapshot?.video);
            sessionMedia.setMediaFrameVisible(isAlertMedia);

            const video = domRefWrapper.video;
            if (video && !video.paused) video.pause();

            playbackPositionRef.current = Number(val);
            resetTimelineClock(Number(val));
            rewindMapToPosition(Number(val), Number(val) <= 0);
            baseMediaHandlers.onTimelineInput?.(val);
            sessionMedia.setTimelineState(prev => ({
                ...prev,
                isLive: false
            }));
        },

        // ═══════════════════════════════════
        // RELÂCHEMENT : isPlayingRef INCHANGÉ
        // ═══════════════════════════════════
        onTimelineChange: (val) => {
            const targetVal = Number(val);

            // Rester systématiquement dans le mode replay au point T sélectionné
            isLiveRef.current = false;
            directMediaTargetRef.current = null;
            directMediaWaitingRef.current = false;
            setReplayMediaWaiting(false);
            setStatusMessage(null);
            playbackPositionRef.current = targetVal;
            resetTimelineClock(targetVal);

            sessionMedia.setTimelineState(prev => ({
                ...prev,
                isLive: false
            }));
            baseMediaHandlers.onTimelineChange?.(targetVal);
            syncMedia(targetVal, true, isPlayingRef.current);

            if (isPlayingRef.current) startPlaybackInterval();
        },

        // ═══════════════════════════════════
        // ACTIVER LE SON : geste utilisateur requis par le navigateur avant
        // toute lecture audio. En Direct, ce geste est capturé par le clic
        // "Continuer" (→ onGoLive). En historique, la lecture démarre seule
        // dès le chargement (pas de "Continuer"), donc rien ne déclenche
        // jamais ce geste : la vidéo reste muette et l'AudioContext reste
        // suspendu indéfiniment. Ce handler capture ce geste explicitement.
        // ═══════════════════════════════════
        onEnableSound: () => {
            const video = domRefWrapper.video;
            if (video?.muted) video.muted = false;
            sessionMedia.resumeAudio();
        }
    };

    return {
        timelineState: sessionMedia.timelineState,
        slaveState: sessionMedia.slaveState,
        rotationMode: sessionMedia.rotationMode,
        toggleRotationMode: sessionMedia.toggleRotationMode,
        statusMessage,
        accessMessage,
        sessionState,
        isHistoricalMode,
        canGoLive,
        mediaHandlers: customMediaHandlers,
        journal,
        timelineMarkers,
        getAppliedRotation: sessionMedia.getAppliedRotation,
        invalidateMapSize: sessionMap.invalidateMapSize,
        hlsLatency: sessionMedia.hlsLatencyRef?.current || 12,
        isDualCameraAvailable: sessionMedia.isDualCameraAvailable,
        selectedCamera: sessionMedia.selectedCamera,
        switchCamera: sessionMedia.switchCamera,
        mediaUrl: sessionMedia.mediaUrl,
        sessionMediaKey: sessionData.sessionMediaKeyRef?.current,
        backendUrl: BACKEND_API_URL
    };
};
